"use client";

import { useMemo, useState } from "react";
import {
  Download,
  FileText,
  FileImage,
  FileSpreadsheet,
  FileCode2,
  FileArchive,
  FileAudio,
  FileVideo,
  File as FileIcon,
  Loader2,
} from "lucide-react";
import { Button } from "../shadcn/button";
import { cn } from "@/lib/utils";
import { getSpotFileUrl } from "@/utils/generic";
import { formatSizeInBytes } from "@/utils/generic";
import type { NoxusDataTypes } from "./noxus-message-types";

type FileAttachment = NoxusDataTypes["file_attachment"];

// Order matters — first match wins. Keep PDFs and Word docs above the
// generic ``text/`` fallback so they don't get the same icon/color.
// Tuple: [regex, icon, text-color, bg-color (-50 tint), border-color (-100 tint)]
//
// tw-safelist:
//   text-sky-500 bg-sky-50 border-sky-100
//   text-purple-500 bg-purple-50 border-purple-100
//   text-pink-500 bg-pink-50 border-pink-100
//   text-red-500 bg-red-50 border-red-100
//   text-blue-500 bg-blue-50 border-blue-100
//   text-orange-500 bg-orange-50 border-orange-100
//   text-warning-500 bg-warning-50 border-warning-100
//   text-emerald-500 bg-emerald-50 border-emerald-100
//   text-violet-500 bg-violet-50 border-violet-100
//   text-slate-500 bg-slate-50 border-slate-100
const TYPE_ICON_MAP: Array<[RegExp, typeof FileIcon, string, string, string]> =
  [
    [/^image\//, FileImage, "text-sky-500", "bg-sky-50", "border-sky-100"],
    [
      /^audio\//,
      FileAudio,
      "text-purple-500",
      "bg-purple-50",
      "border-purple-100",
    ],
    [/^video\//, FileVideo, "text-pink-500", "bg-pink-50", "border-pink-100"],
    [
      /^application\/pdf$/,
      FileText,
      "text-red-500",
      "bg-red-50",
      "border-red-100",
    ],
    [
      /(msword|wordprocessing)/,
      FileText,
      "text-blue-500",
      "bg-blue-50",
      "border-blue-100",
    ],
    [
      /^text\/html/,
      FileCode2,
      "text-orange-500",
      "bg-orange-50",
      "border-orange-100",
    ],
    [
      /(zip|tar|rar|gzip|x-7z)/,
      FileArchive,
      "text-warning-500",
      "bg-warning-50",
      "border-warning-100",
    ],
    [
      /(spreadsheet|excel|csv|x-csv|tab-separated)/,
      FileSpreadsheet,
      "text-emerald-500",
      "bg-emerald-50",
      "border-emerald-100",
    ],
    [
      /(json|xml|yaml|javascript|typescript|x-python|x-sh|x-c|x-java)/,
      FileCode2,
      "text-violet-500",
      "bg-violet-50",
      "border-violet-100",
    ],
    [/markdown/, FileText, "text-slate-500", "bg-slate-50", "border-slate-100"],
    [/^text\//, FileText, "text-slate-500", "bg-slate-50", "border-slate-100"],
  ];

export function pickIcon(contentType: string): {
  Icon: typeof FileIcon;
  iconClass: string;
  bgClass: string;
  borderClass: string;
} {
  for (const [
    pattern,
    Icon,
    iconClass,
    bgClass,
    borderClass,
  ] of TYPE_ICON_MAP) {
    if (pattern.test(contentType))
      return { Icon, iconClass, bgClass, borderClass };
  }
  return {
    Icon: FileIcon,
    iconClass: "text-muted-foreground",
    bgClass: "bg-white",
    borderClass: "border-border",
  };
}

function isInlineRenderable(contentType: string): boolean {
  return contentType.startsWith("image/") || /^text\/html/.test(contentType);
}

function resolveDownloadUrl(url: string, isPublic: boolean): string {
  if (url.startsWith("spot://")) return getSpotFileUrl(url, false, isPublic);
  return url;
}

function resolvePreviewUrl(url: string, isPublic: boolean): string {
  if (url.startsWith("spot://")) return getSpotFileUrl(url, true, isPublic);
  return url;
}

export type PresentedFileWidgetProps = {
  data: FileAttachment;
  isPublic?: boolean;
  className?: string;
};

export function PresentedFileWidget({
  data,
  isPublic = false,
  className,
}: PresentedFileWidgetProps) {
  const { Icon, iconClass, bgClass, borderClass } = useMemo(
    () => pickIcon(data.content_type),
    [data.content_type]
  );

  const downloadUrl = useMemo(
    () => resolveDownloadUrl(data.url, isPublic),
    [data.url, isPublic]
  );

  const inlineUrl = useMemo(
    () => resolvePreviewUrl(data.url, isPublic),
    [data.url, isPublic]
  );

  const sizeLabel =
    typeof data.size === "number" && data.size > 0
      ? formatSizeInBytes(data.size)
      : null;

  const isImage = data.content_type.startsWith("image/");
  const renderInline =
    !!data.show_inline && isInlineRenderable(data.content_type);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    if (!downloadUrl || isDownloading) return;
    setIsDownloading(true);
    // Trigger the download asynchronously so the spinner has a chance to
    // render before the browser kicks the download off; auto-clear after
    // a short delay since the click() doesn't fire a completion event.
    setTimeout(() => {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = data.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsDownloading(false);
    }, 250);
  };

  const caption = data.caption ? (
    <p className="mt-1.5 text-xs text-muted-foreground">{data.caption}</p>
  ) : null;

  // Inline images replace the file card entirely — the image IS the content,
  // with the download affordance revealed on hover.
  if (isImage && renderInline) {
    return (
      <div className={cn("w-full", className)}>
        <div className="group relative w-fit max-w-full overflow-hidden rounded-lg border border-border bg-muted/30">
          <img
            src={inlineUrl}
            alt={data.caption || data.name}
            className="block max-h-[480px] max-w-full object-contain"
          />
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-2 top-2 size-8 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
            onClick={handleDownload}
            disabled={isDownloading}
            title={isDownloading ? "Preparing download..." : "Download"}
            aria-label="Download image"
          >
            {isDownloading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
          </Button>
        </div>
        {caption}
      </div>
    );
  }

  return (
    <>
      {renderInline && (
        <div
          className={cn(
            "mb-2 w-full overflow-hidden rounded-lg border border-border bg-background shadow-sm",
            className
          )}
        >
          {/* sandbox=allow-scripts only — runs Plotly/Chart.js etc. but
              blocks same-origin, top-nav, forms, popups, downloads. */}
          <iframe
            src={inlineUrl}
            className="block w-full h-[480px] border-0 bg-background"
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            title={data.name}
          />
        </div>
      )}
      <div
        className={cn(
          "flex w-full max-w-md items-center gap-3 rounded-lg border border-border bg-white px-3 py-2.5 shadow-xs",
          className
        )}
      >
        <div
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-md border",
            bgClass,
            borderClass,
            iconClass
          )}
        >
          <Icon className="size-5" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className="truncate text-sm font-medium text-foreground"
            title={data.name}
          >
            {data.name}
          </span>
          <span className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <span className="min-w-0 truncate">
              {data.content_type || "file"}
            </span>
            {sizeLabel && (
              <>
                <span aria-hidden className="shrink-0">
                  ·
                </span>
                <span className="shrink-0 whitespace-nowrap">{sizeLabel}</span>
              </>
            )}
          </span>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={handleDownload}
          disabled={isDownloading}
          title={isDownloading ? "Preparing download..." : "Download"}
          aria-label="Download file"
        >
          {isDownloading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </Button>
      </div>
      {caption}
    </>
  );
}
